import type { ReactNode } from 'react';

interface NudgeCardContentProps {
  /** The meaning glyph shown in the icon slot (size-10.5). */
  icon: ReactNode;
  /** Short bold headline hook. */
  headline: string;
  /** The resolved support sentence (the caller resolves it, so this stays presentation-only). */
  body: string;
  /** Solid teal pill label. */
  ctaLabel: string;
  onActivate: () => void;
}

/**
 * The centered content column of a nudge - meaning glyph, a short bold headline over a muted support
 * line, and the solid teal pill CTA. Returned as a fragment (no wrapper element) so the composing
 * frame owns the vertical rhythm: the question-detail NudgeCard drops it into its `gap-4`
 * gradient-border surface (DOM byte-identical to before this extraction), while the event match-gate
 * (ForYouGateCard) drops the SAME column into its header + frost frame at the Figma's `gap-2.5`. One
 * column, two frames - the fb6 reuse round's "same component, not a lookalike".
 *
 * Text niceties (`text-balance` headline, `text-pretty` support) live here so both surfaces inherit
 * them; the `w-full` pill fills whatever content column the frame gives it.
 */
export function NudgeCardContent({
  icon,
  headline,
  body,
  ctaLabel,
  onActivate,
}: NudgeCardContentProps) {
  return (
    <>
      {icon}
      <div className="flex flex-col items-center gap-1.5">
        <h3 className="text-lg font-bold leading-normal text-balance text-black">{headline}</h3>
        <p className="max-w-xs text-sm leading-normal text-pretty text-neutral-600">{body}</p>
      </div>
      {/* Mirrors the ProfileCompletionModal's CTA classes; h-10 (vs the modal's h-9.5) meets the
          >= 40px hit-area rule. */}
      <button
        type="button"
        onClick={onActivate}
        className="h-10 w-full rounded-full bg-mb-teal font-fira text-base font-medium tracking-wide text-mb-ink transition-colors hover:bg-mb-teal-dark"
      >
        {ctaLabel}
      </button>
    </>
  );
}
